import React from 'react'
import { Box, Grid, Stack, Typography, useMediaQuery } from '@mui/material'

import { GRAY_900, RhetorikFontFamily } from 'constants/styling/theme'
import type { ResolvedShellProduct } from 'models/purchaseFlowRevamp'
import { ProductCard } from './ProductCard.component'

interface Props {
  /** Category id — also used as the DOM id anchor (`cat-{id}`). */
  id: string
  /** Display title above the grid. */
  title: string
  /** Products rendered in this section. */
  products: ResolvedShellProduct[]
  /** Handler for product selection — bubbles up from each card. */
  onSelectProduct: (product: ResolvedShellProduct) => void
  onShowProductInfo: (product: ResolvedShellProduct) => void
  /** DOM id prefix. Defaults to 'cat-' (kept in sync with useCategoryScrollSync). */
  domIdPrefix?: string
  /**
   * CSS scroll-margin-top for the anchor (A#2). Lets the catalogue page offset
   * click-to-scroll by the combined sticky-header height so the section title
   * stays visible after the jump. Defaults to 8rem.
   */
  scrollMarginTop?: string | Record<string, string>
}

/**
 * One catalogue category section — heading + responsive product grid.
 *
 * The grid comes in the designer's two shapes (prototype `.pf-grid` /
 * `.pf-grid--thumb`), decided per SECTION rather than per card:
 *
 *   - a section whose products have artwork lays out TWO per row, each card a
 *     180px thumbnail beside its text;
 *   - a section with none — documents, which have no artwork by design — lays
 *     out THREE per row, each card a product glyph above stacked text.
 *
 * Deciding per section is what keeps a row uniform: mixing the shapes would put
 * a 180px column on some cards in a row and not others. A section that has SOME
 * artwork still uses the thumbnail shape, and the cards without it render the
 * tile's own empty state, which is what the design specifies for that case.
 *
 * The wrapping `<Box>` carries `id={prefix}{id}` so the scroll-sync hook's
 * IntersectionObserver can track when this section enters the viewport.
 */
/**
 * Between the width where the cart rail appears and the width where the reading
 * column reaches its cap, the column is too narrow to hold two cards side by
 * side — one per row reads better than two cramped ones. Outside this band the
 * card counts are unchanged.
 */
const SINGLE_COLUMN_QUERY = '(min-width:801px) and (max-width:1100px)'

export const CategorySection: React.FC<Props> = ({
  id,
  title,
  products,
  onSelectProduct,
  onShowProductInfo,
  domIdPrefix = 'cat-',
  scrollMarginTop = '8rem',
}) => {
  const singleColumn = useMediaQuery(SINGLE_COLUMN_QUERY)

  if (products.length === 0) return null

  // `kind: 'none'` is the wire's explicit "no artwork" and must not claim a
  // thumbnail column, so it counts as no preview here.
  const hasArtwork = products.some((product) => product.preview && product.preview.kind !== 'none')
  const layout = hasArtwork ? 'thumb' : 'icon'

  return (
    <Box id={`${domIdPrefix}${id}`} sx={{ scrollMarginTop }}>

      <Stack gap={2}>

        <Typography
          variant="text-2xl"
          fontWeight={400}
          color={GRAY_900}
          fontFamily={RhetorikFontFamily}
        >
          {title}
        </Typography>

        {/* 32px gutters, as the prototype's grid gap — the cards carry no padding
            of their own, so the gap IS the spacing between them. */}
        <Grid container spacing={3.2}>
          {products.map((product) => (
            <Grid
              key={product.id}
              item
              xs={12}
              sm={singleColumn ? 12 : (hasArtwork ? 12 : 6)}
              md={singleColumn ? 12 : (hasArtwork ? 6 : 4)}
            >

              <ProductCard
                product={product}
                onSelect={onSelectProduct}
                onShowInfo={onShowProductInfo}
                layout={layout}
              />

            </Grid>
          ))}
        </Grid>

      </Stack>

    </Box>
  )
}
